// ===================================
// ERROR BOUNDARY COMPONENT - src/components/ErrorBoundary.tsx
// ===================================
import React, { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    // Log to external service in production
    if (process.env.NODE_ENV === "production") {
      // You can add error reporting service here
    }
  }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen bg-[#020617] flex flex-col justify-center items-center px-4">
          <div className="max-w-md w-full bg-[#0f172a] rounded-lg shadow-lg p-8 text-center">
            <div className="mb-6">
              <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Oops! Something went wrong
              </h1>
              <p className="text-[#64748b] mb-4">
                We encountered an unexpected error. Don't worry, your data is
                safe.
              </p>
            </div>

            <div className="space-y-3 mb-6">
              <button
                onClick={() => window.location.reload()}
                className="w-full bg-[#8b3dff] text-white px-4 py-3 rounded-lg hover:bg-[#7c3aed] transition-colors flex items-center justify-center space-x-2 font-medium"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Refresh Page</span>
              </button>

              <button
                onClick={() => (window.location.href = "/dashboard")}
                className="w-full bg-[#020617] text-[#64748b] px-4 py-3 rounded-lg hover:text-[#8b3dff] transition-colors flex items-center justify-center space-x-2 font-medium"
              >
                <Home className="w-4 h-4" />
                <span>Go to Dashboard</span>
              </button>

              <button
                onClick={() =>
                  this.setState({
                    hasError: false,
                    error: undefined,
                    errorInfo: undefined,
                  })
                }
                className="w-full text-[#8b3dff] px-4 py-2 rounded-lg hover:text-[#64748b] transition-colors text-sm"
              >
                Try Again
              </button>
            </div>

            {process.env.NODE_ENV === "development" && this.state.error && (
              <details className="text-left bg-[#020617] rounded-lg p-4">
                <summary className="text-sm text-[#64748b] cursor-pointer mb-3 font-medium">
                  🔍 Technical Details (Development Mode)
                </summary>
                <div className="space-y-3">
                  <div className="bg-red-50 border border-red-200 rounded p-3">
                    <div className="text-xs font-semibold text-red-800 mb-1">
                      Error Message:
                    </div>
                    <div className="text-xs text-red-700 font-mono">
                      {this.state.error.message}
                    </div>
                  </div>

                  {this.state.error.stack && (
                    <div className="bg-gray-100 border border-gray-200 rounded p-3">
                      <div className="text-xs font-semibold text-gray-800 mb-1">
                        Stack Trace:
                      </div>
                      <pre className="text-xs text-[#64748b] font-mono whitespace-pre-wrap overflow-auto max-h-32">
                        {this.state.error.stack}
                      </pre>
                    </div>
                  )}

                  {this.state.errorInfo && (
                    <div className="bg-blue-50 border border-blue-200 rounded p-3">
                      <div className="text-xs font-semibold text-blue-800 mb-1">
                        Component Stack:
                      </div>
                      <pre className="text-xs text-[#7c3aed] font-mono whitespace-pre-wrap overflow-auto max-h-32">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </div>
                  )}
                </div>
              </details>
            )}

            <div className="mt-6 text-xs text-gray-500">
              If this problem persists, please contact support.
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
